import { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';

import Modal from './Modal';
import ProjectAllocationForm from './ProjectAllocationForm';
import ConfirmPrompt from './ConfirmPrompt';
import AddRequiredLicenseForm from './AddRequiredLicenseForm';
import AddProjectForm from './AddProjectForm';
import UpdateProjectForm from './UpdateProjectForm';
import AdminProjectView from './AdminProjectView';
import TableLoader from './TableLoader';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import SectionHeader from './SectionHeader';
import FilterSelect from './FilterSelect';

import projectService from '../services/projectService';
import useProjects from '../hooks/useProjects';
import useDebounce from '../hooks/useDebounce';

import projectStatuses from '../data/projectStatuses.json';

import { formatDateToSydneyTimeZone } from '../utils/dateUtils';

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const Projects = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput);

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  useEffect(() => { setPage(1); }, [search, selectedStatus, dateRange]);

  const [isProjectViewOpen, setIsProjectViewOpen] = useState(false);
  const [isAllocationsViewOpen, setIsAllocationsViewOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isAddLicenseModalOpen, setIsAddLicenseModalOpen] = useState(false);
  const [isAddProjectModalOpen, setIsaddProjectModalOpen] = useState(false);
  const [isUpdateProjectModalOpen, setIsUpdateProjectModalOpen] = useState(false);

  const { data, isLoading: isLoadingProjects, mutate } = useProjects({
    status: selectedStatus?.value || null,
    startDate: dateRange?.[0] || null,
    endDate: dateRange?.[1] || null,
    search,
    sortField,
    sortOrder,
    page,
    perPage
  });

  const projects = data?.projects;
  const projectCount = data?.count;

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const onProjectRowClicked = (row) => {
    setSelectedProject(row);
    setIsProjectViewOpen(true);
  }

  const handleAllocate = (row) => {
    setSelectedProject(row);
    setIsAllocationsViewOpen(true);
  };

  const handleAllocationCompleted = async () => {
    setIsAllocationsViewOpen(false);
    mutate();
  };

  const handleUpdateProjectClicked = (row) => {
    setIsUpdateProjectModalOpen(true);
  }

  const handleUpdateProject = async (updateData) => {
    try {
      setIsUpdateProjectModalOpen(false);
      setIsProjectViewOpen(false);

      const { project } = await projectService.updateProject({
        projectId: selectedProject._id,
        updateData
      });

      mutate();

      setIsUpdateProjectModalOpen(false);
      setIsProjectViewOpen(false);
      setSelectedProject(null);

      toast.success(`${project.name} updated`, { icon: '🚀' });
    } catch (error) {
      const { message } = error?.response?.data;

      toast.error(message || 'An error occurred while updating labourer');
    }
  }

  const handleDelete = (row) => {
    setSelectedProject(row);
    setIsConfirmDeleteModalOpen(true);
  }

  const handleCreateProject = async (payload) => {
    try {
      setIsaddProjectModalOpen(false);

      const { project } = await projectService.createProject(payload);
      mutate();

      toast.success(`${project.name} created`, { icon: '🚀' });
    } catch (error) {
      toast.error('Failed to create project');
    }
  }

  const deleteProject = async (project) => {
    try {
      setIsConfirmDeleteModalOpen(false);
      setIsProjectViewOpen(false);
      setSelectedProject(null);

      await projectService.deleteProject(project._id);
      mutate();

      toast.success(`${project.name} deleted`, { icon: '🚀' });
    } catch (error) {
      const { message } = error?.response?.data;

      toast.error(message || 'An error occurred while deleting labourer');
    }
  }

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      grow: 2,
      sortable: true,
      sortField: 'name'
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      sortField: 'status',
      conditionalCellStyles: [
        {
          when: row => row.status === 'Active',
          classNames: ['text-ase-blue']
        },
        {
          when: row => row.status === 'Canceled',
          classNames: ['text-red-600']
        },
        {
          when: row => row.status === 'Pending',
          classNames: ['text-yellow-600']
        },
        {
          when: row => row.status === 'Completed',
          classNames: ['text-green-600']
        }
      ]
    },
    {
      name: 'Start Date',
      selector: row => row.startDate,
      format: row => formatDateToSydneyTimeZone(row.startDate),
      sortable: true,
      sortField: 'startDate'
    },
    {
      name: 'End Date',
      selector: row => row.endDate,
      format: row => formatDateToSydneyTimeZone(row.endDate),
      sortable: true,
      sortField: 'endDate'
    },
    {
      name: '',
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleAllocate(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white text-nowrap'
          >
            Allocate
          </button>

          <button
            onClick={() => handleDelete(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white text-nowrap'
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Projects | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Projects'
          actionsLeft={
            <button
              onClick={() => setIsaddProjectModalOpen(true)}
              className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
            >
              Add
            </button>
          }
          actionsRight={(
            <div className='flex flex-col md:flex-row md:items-center gap-2 w-full'>
              <div className="relative flex items-center">
                <MagnifyingGlassIcon className="absolute left-[10px] h-3 w-3 text-gray-600 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="!pl-[28px] pr-2 text-xs !h-[25px] !border border-none rounded-md bg-white text-gray-700 placeholder-gray-400 focus:outline-none w-40"
                />
              </div>

              <p className='text-white text-xs'>Status:</p>
              <div className='text-xs  h-[24px]'>
                <FilterSelect
                  options={projectStatuses}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  placeholder='Select status'
                />
              </div>

              <p className='text-white text-xs'>Period:</p>

              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format='dd/MM/y'
                className="filter-date-range"
              />
            </div>
          )}
        />

        <DataTable
          columns={columns}
          data={projects}
          striped
          highlightOnHover
          pointerOnHover
          onRowClicked={onProjectRowClicked}
          progressPending={isLoadingProjects}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={projectCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />

        <Modal isOpen={isAddLicenseModalOpen} overflowType="visible">
          <AddRequiredLicenseForm onCloseClicked={e => setIsAddLicenseModalOpen(false)} />
        </Modal>

        <Modal isOpen={isAddProjectModalOpen}>
          <AddProjectForm
            onSubmit={handleCreateProject}
            onCloseClicked={e => setIsaddProjectModalOpen(false)}
          />
        </Modal>

        <Modal isOpen={isUpdateProjectModalOpen}>
          <UpdateProjectForm
            project={selectedProject}
            onSubmit={handleUpdateProject}
            onCloseClicked={e => setIsUpdateProjectModalOpen(false)}
          />
        </Modal>

        <Modal isOpen={isProjectViewOpen}>
          <AdminProjectView
            project={selectedProject}
            onAddAllocationClicked={() => setIsAllocationsViewOpen(true)}
            onAddLicenseClicked={() => setIsAddLicenseModalOpen(true)}
            onUpdateClicked={() => handleUpdateProjectClicked(true)}
            onDeleteClicked={() => setIsConfirmDeleteModalOpen(true)}
            onCloseClicked={() => setIsProjectViewOpen(false)}
          />
        </Modal>

        <Modal isOpen={isAllocationsViewOpen} overflowType='visible'>
          <ProjectAllocationForm
            project={selectedProject}
            onAllocate={handleAllocationCompleted}
            onCloseClicked={() => setIsAllocationsViewOpen(false)}
          />
        </Modal>

        <Modal isOpen={isConfirmDeleteModalOpen}>
          <ConfirmPrompt
            message={`Are you sure you want to delete the ${selectedProject?.name} project?`}
            onConfirm={() => deleteProject(selectedProject)}
            onCancel={() => setIsConfirmDeleteModalOpen(null)}
          />
        </Modal>
      </div>
    </>
  )
}

export default Projects;
